import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Download, XCircle, DollarSign, Banknote, Smartphone, Building2, CreditCard, FileText, Clock, CheckCircle, AlertCircle, Mail, MessageCircle, Loader2 } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { paymentsReceivedAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const PAYMENT_MODE_CONFIG = {
  cash: { icon: Banknote, label: 'Cash', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  upi: { icon: Smartphone, label: 'UPI', bg: 'bg-purple-100', text: 'text-purple-700' },
  bank_transfer: { icon: Building2, label: 'Bank Transfer', bg: 'bg-blue-100', text: 'text-blue-700' },
  card: { icon: CreditCard, label: 'Card', bg: 'bg-amber-100', text: 'text-amber-700' },
  cheque: { icon: FileText, label: 'Cheque', bg: 'bg-slate-100', text: 'text-slate-700' },
  wallet: { icon: DollarSign, label: 'Wallet', bg: 'bg-cyan-100', text: 'text-cyan-700' },
  other: { icon: DollarSign, label: 'Other', bg: 'bg-gray-100', text: 'text-gray-700' }
};

const STATUS_CONFIG = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed' },
  voided: { bg: 'bg-red-100', text: 'text-red-700', label: 'Voided' },
  refunded: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Refunded' }
};

const getReceiptNo = (p) => p?.receipt_no || p?.receipt_number || '';
const getCustomerName = (p) => p?.customer_name || p?.party_name || '';
const getCustomerPhone = (p) => p?.customer_phone || p?.party_phone || '';
const getTransactionRef = (p) => p?.transaction_reference || p?.transaction_id || '';

const PaymentDetailPage = () => {
  const { businessId, id: paymentId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid && paymentId) fetchPayment();
  }, [bid, paymentId]);

  const fetchPayment = async () => {
    setLoading(true);
    try {
      const response = await paymentsReceivedAPI.get(paymentId);
      setPayment(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Payment not found'));
      navigate(`/business/${bid}/payments-received`);
    } finally {
      setLoading(false);
    }
  };

  const handleVoid = () => {
    setVoidReason('');
    setConfirmModal({
      title: 'Void Payment',
      description: `Void receipt ${getReceiptNo(payment)} for \u20B9${(payment.amount_received || 0).toLocaleString('en-IN')}? This will reverse any invoice allocations.`,
      confirmLabel: 'Void Payment',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      isVoidFlow: true,
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await paymentsReceivedAPI.void(paymentId, reason);
          toast.success('Payment voided');
          setVoidReason('');
          fetchPayment();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error voiding payment'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await paymentsReceivedAPI.pdf(paymentId);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Receipt_${getReceiptNo(payment)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error downloading receipt'));
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await paymentsReceivedAPI.sendEmail(paymentId);
      const data = response.data;
      toast.success(data.message || 'Email sent');
      if (data.note) toast(data.note, { icon: 'info' });
      fetchPayment();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error sending email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      const response = await paymentsReceivedAPI.sendWhatsApp(paymentId);
      const data = response.data;
      toast.success(data.message || 'WhatsApp sent');
      if (data.note) toast(data.note, { icon: 'info' });
      fetchPayment();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error sending WhatsApp'));
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F7F8FA]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-emerald-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!payment) return null;

  const modeConfig = PAYMENT_MODE_CONFIG[payment.payment_mode] || PAYMENT_MODE_CONFIG.other;
  const statusConfig = STATUS_CONFIG[payment.status] || STATUS_CONFIG.draft;
  const ModeIcon = modeConfig.icon;
  const allocs = payment.allocations || payment.invoices_allocated || [];

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(`/business/${bid}/payments-received`)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-semibold text-slate-800">{getReceiptNo(payment)}</h1>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                    {statusConfig.label}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Recorded on {new Date(payment.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {payment.status === 'draft' && (
                <button onClick={() => navigate(`/business/${bid}/payments-received/${paymentId}/edit`)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg text-[13px] font-medium transition-colors">
                  <Edit2 className="w-4 h-4" /> Edit
                </button>
              )}
              {payment.status === 'completed' && (
                <>
                  <button onClick={handleSendEmail} disabled={sendingEmail}
                    className="inline-flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-[13px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {sendingEmail ? 'Sending...' : 'Email'}
                  </button>
                  <button onClick={handleSendWhatsApp}
                    className="inline-flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-[13px] font-medium transition-colors">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </button>
                  <button onClick={handleVoid}
                    className="inline-flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-[13px] font-medium transition-colors">
                    <XCircle className="w-4 h-4" /> Void
                  </button>
                </>
              )}
              <button onClick={handleDownloadPdf}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-[13px] font-medium transition-colors">
                <Download className="w-4 h-4" /> Download Receipt
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-12 gap-6">

            {/* LEFT COLUMN */}
            <div className="col-span-8 space-y-6">

              {/* Transaction Details */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/60">
                <h3 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-4">Transaction Details</h3>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-[12px] text-slate-500 mb-1">Customer</p>
                      <p className="text-[15px] font-medium text-slate-800">{getCustomerName(payment)}</p>
                      {getCustomerPhone(payment) && <p className="text-[13px] text-slate-500">{getCustomerPhone(payment)}</p>}
                    </div>
                    <div>
                      <p className="text-[12px] text-slate-500 mb-1">Payment Date</p>
                      <p className="text-[14px] text-slate-800">
                        {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[12px] text-slate-500 mb-1">Collected By</p>
                      <p className="text-[14px] text-slate-800">{payment.collected_by_name || '-'}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-[12px] text-slate-500 mb-1">Payment Mode</p>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium ${modeConfig.bg} ${modeConfig.text}`}>
                        <ModeIcon className="w-4 h-4" /> {modeConfig.label}
                      </span>
                    </div>
                    {getTransactionRef(payment) && (
                      <div>
                        <p className="text-[12px] text-slate-500 mb-1">Transaction Reference</p>
                        <p className="text-[14px] text-slate-800 font-mono">{getTransactionRef(payment)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[12px] text-slate-500 mb-1">Deposit To</p>
                      <p className="text-[14px] text-slate-800">{payment.deposit_to_account || 'Cash'}</p>
                    </div>
                  </div>
                </div>

                {(payment.upi_app || payment.bank_name || payment.cheque_number || payment.card_last4) && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-[12px] text-slate-500 mb-2">Additional Details</p>
                    <div className="flex flex-wrap gap-4 text-[13px]">
                      {payment.upi_app && <span><span className="text-slate-500">UPI App:</span> {payment.upi_app}</span>}
                      {payment.bank_name && <span><span className="text-slate-500">Bank:</span> {payment.bank_name}</span>}
                      {payment.cheque_number && <span><span className="text-slate-500">Cheque No:</span> {payment.cheque_number}</span>}
                      {payment.cheque_date && <span><span className="text-slate-500">Cheque Date:</span> {payment.cheque_date}</span>}
                      {payment.card_last4 && <span><span className="text-slate-500">Card:</span> ****{payment.card_last4}</span>}
                      {payment.card_type && <span><span className="text-slate-500">Card Type:</span> {payment.card_type}</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Allocations */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Invoice Allocations</h3>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${
                    payment.allocation_status === 'fully_allocated' ? 'bg-emerald-100 text-emerald-700' :
                    payment.allocation_status === 'partially_allocated' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {payment.allocation_status === 'fully_allocated' ? 'Fully Allocated' :
                     payment.allocation_status === 'partially_allocated' ? 'Partially Allocated' :
                     'Unallocated'}
                  </span>
                </div>

                {Array.isArray(allocs) && allocs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-emerald-50/60 border-b border-emerald-100">
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-emerald-700 uppercase">Invoice #</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-emerald-700 uppercase">Invoice Date</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-emerald-700 uppercase">Invoice Total</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-emerald-700 uppercase">Amount Applied</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allocs.map((alloc, index) => (
                          <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="px-4 py-3">
                              <button onClick={() => navigate(`/business/${bid}/sales/invoices/${alloc.invoice_id}`)}
                                className="text-[13px] font-medium text-blue-600 hover:text-blue-700 hover:underline">
                                {alloc.invoice_number}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-[13px] text-slate-600">
                              {alloc.invoice_date ? new Date(alloc.invoice_date).toLocaleDateString('en-IN') : '-'}
                            </td>
                            <td className="px-4 py-3 text-[13px] text-slate-600 text-right">
                              {'\u20B9'}{(alloc.invoice_total || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="px-4 py-3 text-[13px] font-medium text-emerald-600 text-right">
                              {'\u20B9'}{(alloc.allocated_amount || 0).toLocaleString('en-IN')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50">
                          <td colSpan={3} className="px-4 py-3 text-[13px] font-medium text-slate-700 text-right">
                            Total Allocated
                          </td>
                          <td className="px-4 py-3 text-[14px] font-semibold text-emerald-600 text-right">
                            {'\u20B9'}{(payment.allocated_amount || 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                    <p className="text-[13px] text-slate-600">No invoices allocated</p>
                    <p className="text-[12px] text-amber-600 mt-1">This payment is recorded as advance</p>
                  </div>
                )}
              </div>

              {payment.notes && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/60">
                  <h3 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-3">Notes</h3>
                  <p className="text-[13px] text-slate-600 whitespace-pre-wrap">{payment.notes}</p>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN */}
            <div className="col-span-4 space-y-6">

              {/* Amount Summary */}
              <div className="bg-amber-50 rounded-xl p-5 shadow-sm border border-amber-200/60">
                <h3 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-4">Amount Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Amount Received</span>
                    <span className="text-[16px] font-bold text-slate-800">{'\u20B9'}{(payment.amount_received || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Allocated</span>
                    <span className="text-emerald-600 font-medium">{'\u20B9'}{(payment.allocated_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>

                  <div className="border-t border-amber-200 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-800">Unallocated</span>
                      <span className={`text-[18px] font-bold ${((payment.unallocated_amount || payment.excess_amount || 0) > 0) ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {'\u20B9'}{(payment.unallocated_amount || payment.excess_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Void Info */}
              {payment.status === 'voided' && (
                <div className="bg-red-50 rounded-xl p-5 shadow-sm border border-red-200/60">
                  <h3 className="text-[13px] font-semibold text-red-700 uppercase tracking-wide mb-3">Voided</h3>
                  <div className="space-y-2 text-[13px]">
                    {payment.voided_at && (
                      <p><span className="text-slate-500">Voided on:</span> {new Date(payment.voided_at).toLocaleString('en-IN')}</p>
                    )}
                    {payment.void_reason && (
                      <p><span className="text-slate-500">Reason:</span> {payment.void_reason}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Activity Timeline */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <h3 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-4">Activity Log</h3>
                <div className="space-y-4">
                  {(payment.activity_log || payment.audit_log || []).map((log, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 ${
                        log.action === 'created' ? 'bg-blue-500' :
                        log.action === 'updated' ? 'bg-amber-500' :
                        log.action === 'voided' ? 'bg-red-500' :
                        log.action === 'email_sent' || log.action === 'whatsapp_sent' ? 'bg-purple-500' :
                        'bg-slate-400'
                      }`}></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-800 capitalize">{log.action?.replace('_', ' ')}</p>
                        <p className="text-[11px] text-slate-500">
                          {log.user_name} {log.timestamp ? `\u2022 ${new Date(log.timestamp).toLocaleString('en-IN')}` : ''}
                        </p>
                        {log.details && <p className="text-[11px] text-slate-500 mt-0.5">{log.details}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        confirmDisabled={confirmModal?.isVoidFlow ? !voidReason.trim() : false}
        onConfirm={() => confirmModal?.onConfirm?.(voidReason)}
        onClose={() => { setConfirmModal(null); setVoidReason(''); }}
      >
        {confirmModal?.isVoidFlow && (
          <textarea
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="e.g. Customer returned goods, duplicate payment, incorrect amount..."
            rows={3}
            className="w-full mt-3 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none"
          />
        )}
      </ConfirmModal>
    </div>
  );
};

export default PaymentDetailPage;
